import { useCallback, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SignInButton, useUser } from "../hooks/useAuthCompat";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";
import { useApi, publicApi } from "../hooks/useApi";
import { useCart } from "../context/CartContext";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

export default function Checkout() {
  const [params] = useSearchParams();
  const buyNow = params.get("buyNow");
  const { isSignedIn, isLoaded } = useUser();
  const api = useApi();
  const { itemIds } = useCart();
  const [checkoutError, setCheckoutError] = useState(null);
  const { data: catalog } = useQuery({ queryKey: ["books"], queryFn: async () => (await publicApi.get("/books")).data });

  const productIds = useMemo(() => (buyNow ? [buyNow] : itemIds), [buyNow, itemIds]);
  const items = productIds.map((id) => catalog?.find((b) => b.id === id)).filter(Boolean);
  const subtotal = items.reduce((sum, b) => sum + b.price, 0);

  const fetchClientSecret = useCallback(async () => {
    try {
      const { data } = await api.post("/checkout/session", { product_ids: productIds, origin_url: window.location.origin });
      if (data.already_owned?.length && !data.client_secret) {
        setCheckoutError(`You already own: ${data.already_owned.join(", ")}`);
        return new Promise(() => {}); // never resolves — error state takes over the view
      }
      return data.client_secret;
    } catch (e) {
      setCheckoutError(e?.response?.data?.detail || e.message || "Checkout could not be started.");
      return new Promise(() => {}); // never resolves — error state takes over the view
    }
  }, [api, productIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoaded) {
    return (
      <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="checkout-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-white/60" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="checkout-signin-required">
        <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-3">Sign In Required</p>
        <h1 className="font-display text-display-sm mb-4">Sign in to complete your purchase</h1>
        <p className="text-white/70 mb-8">Your cart is saved — sign in or create an account to continue to checkout.</p>
        <SignInButton>
          <button data-testid="checkout-signin-button" className="bg-white text-black px-6 py-3 text-sm uppercase tracking-wide hover:bg-white/80 transition-colors duration-200">
            Sign In
          </button>
        </SignInButton>
      </div>
    );
  }

  if (!productIds.length || !items.length) {
    return (
      <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-display-sm mb-4">Your cart is empty</h1>
        <Link to="/books" data-testid="checkout-browse-books-link" className="text-xs uppercase tracking-wide text-brandAccent underline">Browse the bookstore</Link>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="checkout-unconfigured">
        <h1 className="font-display text-display-sm mb-4">Checkout is not configured yet</h1>
        <p className="text-white/70">A Stripe publishable key is required to enable native checkout.</p>
      </div>
    );
  }

  if (checkoutError) {
    return (
      <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="checkout-session-error">
        <h1 className="font-display text-display-sm mb-4">We couldn't start checkout</h1>
        <p className="text-white/70 mb-8">{checkoutError}</p>
        <Link to="/library" data-testid="checkout-error-library-link" className="text-xs uppercase tracking-wide text-brandAccent underline mr-6">Go to My Library</Link>
        <Link to="/books" data-testid="checkout-error-books-link" className="text-xs uppercase tracking-wide text-brandAccent underline">Browse the bookstore</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <p className="text-xs uppercase tracking-[0.2em] text-textSecondary mb-2">Secure Checkout</p>
      <h1 className="font-display text-display-sm md:text-display-md mb-10">Endure Beyond Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2" data-testid="checkout-embedded-container">
          <EmbeddedCheckoutProvider key={productIds.join(",")} stripe={stripePromise} options={{ fetchClientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
        <div className="border border-white/15 p-6 h-fit">
          <p className="text-sm uppercase tracking-wide text-white/70 mb-4">Order Summary</p>
          <div className="space-y-3">
            {items.map((b) => (
              <div key={b.id} data-testid={`checkout-summary-item-${b.slug}`} className="flex justify-between text-sm">
                <div>
                  <span className="text-white/80">{b.title}</span>
                  <p data-testid={`checkout-delivery-format-${b.slug}`} className="text-[10px] uppercase tracking-wide text-white/50 mt-0.5">
                    {b.pdf_available ? "In-app reading + PDF download" : (b.pdf_customer_message || "PDF pending approval")}
                  </p>
                </div>
                <span>${b.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 mt-4 pt-4 flex justify-between text-sm">
            <span className="text-white/70">Subtotal</span>
            <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-white/60 mt-4">Sales tax, where applicable, is calculated by Stripe at payment time.</p>
        </div>
      </div>
    </div>
  );
}
